import React from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Card,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, TimePicker, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import Loader from "@icarius-common/loader";
import FileInput from "@icarius-common/fileUpload/fileInput";
import FileList from "@icarius-common/fileUpload/fileList";
import TextEditor from "@icarius-common/textEditor";
import { DeleteIcon } from "@icarius-icons/index";
import { getYesNoArray } from "@icarius-utils/constants";
import QuestionsPage from "@icarius-pages/coursesMaster/components/coursePage/activityFormDialog/questionsPage";
import useHandleForm from "./useHandleForm";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { padding: 10 };

const ActivityFormDialog = (props) => {

  const {
    open,
    readOnly,
    isLoading,
    data,
    nextOrder,
    modules,
    activities,
    activityTypes,
    media,
    questionTypes,
    sessionTypes,
    invalidFiles,
    submitCallback,
    handleClose,
    handleClearInvalidFiles,
  } = props;

  const dispatch = useDispatch();

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    setSessionItem,
    deleteSessionItem,
    setAttemptItem,
    submit,
  } = useHandleForm(data, submitCallback, openValidationError);

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {getLocalizedString(isCreate ? "createActivity" : readOnly ? "viewActivity" : "updateActivity")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent>
          <Loader open={isLoading} />
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextField
                disabled={readOnly}
                required
                fullWidth
                margin={"none"}
                label={getLocalizedString("name")}
                value={formData.name}
                onChange={(e) => setFormValue(e.target.value, "name")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, paddingTop: 20 }}>
              <TextFieldMultiline
                disabled={readOnly}
                required
                fullWidth
                label={getLocalizedString("description")}
                rows={4}
                margin={"none"}
                value={formData.description}
                onChange={(e) => setFormValue(e.target.value, "description")}
                inputProps={{ spellCheck: 'false' }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <TimePicker
                  disabled={readOnly}
                  required
                  fullWidth
                  ampm={false}
                  label={getLocalizedString("length")}
                  value={formData.length}
                  onChange={(e) => setFormValue(e, "length")}
                  cancelLabel={getLocalizedString("cancel")}
                  invalidDateMessage=""
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-type`}>{getLocalizedString("type")}</InputLabel>
                <Select
                  disabled={readOnly}
                  value={formData.type}
                  labelId={`label-type`}
                  id={`select-type`}
                  onChange={(e) => setFormValue(e.target.value, "type")}
                  margin={"none"}
                >
                  {
                    activityTypes.map((item) => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}
                      >
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-module`}>{getLocalizedString("module")}</InputLabel>
                <Select
                  disabled={readOnly}
                  value={formData.module}
                  labelId={`label-module`}
                  id={`select-module`}
                  onChange={(e) => setFormValue(e.target.value, "module")}
                  margin={"none"}
                >
                  {
                    modules.map((item) => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                disabled={readOnly}
                required
                fullWidth
                margin={"none"}
                label={getLocalizedString("order")}
                InputProps={{ inputComponent: CustomNumberFormat }}
                value={formData.order}
                inputProps={{ min: 1, max: 999, maxLength: 3, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 999 }}
                onChange={(e) => setFormValue(e.target.value, "order")}
              />
              <Typography className="whiteText" variant="caption" style={{ marginTop: 5 }}>
                {getLocalizedString("nextOrderActivity", { nextOrder })}
              </Typography>
            </Grid>
            <Grid container item alignItems="flex-start" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel shrink id={`label-activityDependency`}>{getLocalizedString("activityDependency")}</InputLabel>
                <Select
                  disabled={readOnly}
                  displayEmpty
                  value={formData.activityDependency}
                  labelId={`label-activityDependency`}
                  id={`select-activityDependency`}
                  onChange={(e) => setFormValue(e.target.value, "activityDependency")}
                  margin={"none"}
                >
                  <MenuItem className={"whiteText"} value={""}>
                    {getLocalizedString("noDependency")}
                  </MenuItem>
                  {
                    activities
                      .filter((activity) => activity.id !== data?.id)
                      .map((item) => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.id}
                          value={item.id}
                        >
                          {item.name}
                        </MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <Card style={{ width: "100%", padding: 16 }}>
                <Grid container item alignItems="center" xs={12} style={gridStyle}>
                  <FileInput
                    disabled={readOnly}
                    multiple
                    label={getLocalizedString("selectResources")}
                    acceptedFormats={[
                      ".jpg",
                      ".bmp",
                      ".png",
                      ".jpeg",
                      ".rar",
                      ".zip",
                      ".gz",
                      ".tar",
                      ".xls",
                      ".xlsx",
                      ".txt",
                      ".rtf",
                      ".doc",
                      ".docx",
                      ".pdf",
                      ".html",
                      ".ppt",
                      ".pptx",
                    ]}
                    handleChange={(files) => setFormValue(files, "resources")}
                    id={"resources"}
                    preSubmitCallback={handleClearInvalidFiles}
                  />
                  <FileList
                    disabled={readOnly}
                    files={formData.resources}
                    filesWithError={invalidFiles}
                    handleClick={(file) => setFormValue(file, "removeResources")}
                  />
                </Grid>
              </Card>
            </Grid>
            {
              formData.type &&
              <>
                {
                  ["E"].includes(formData.type) &&
                  <>
                    <QuestionsPage
                      readOnly={readOnly}
                      questions={formData.questions}
                      questionTypes={questionTypes}
                      submitCallback={(questions) => setFormValue(questions, "questions")}
                    />
                    <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, marginTop: 10 }}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel required id={`label-questionsAmountToUse`}>{getLocalizedString("questionsAmountToUse")}</InputLabel>
                        <Select
                          disabled={readOnly}
                          value={formData.questionsAmountToUse}
                          labelId={`label-questionsAmountToUse`}
                          id={`select-questionsAmountToUse`}
                          onChange={(e) => setFormValue(e.target.value, "questionsAmountToUse")}
                          margin={"none"}
                        >
                          {
                            Array.from({ length: formData.questions.length }, (_, i) => i + 1).map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item}
                                value={item}
                              >
                                {item}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                      <Typography className="whiteText" variant="caption">
                        {getLocalizedString("activityQuestionsAmountToUseHelperText")}
                      </Typography>
                    </Grid>
                    <Grid container item alignItems="flex-start" xs={12} sm={3} style={gridStyle}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel id={`label-useRandomOrder`}>{getLocalizedString("useRandomOrder")}</InputLabel>
                        <Select
                          disabled={readOnly}
                          value={formData.useRandomOrder ? "Y" : "N"}
                          labelId={`label-useRandomOrder`}
                          id={`select-useRandomOrder`}
                          onChange={(e) => setFormValue(e.target.value === "Y", "useRandomOrder")}
                          margin={"none"}
                        >
                          {
                            getYesNoArray().map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item.key}
                                value={item.key}
                              >
                                {item.value}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={9} style={gridStyle}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel id={`label-useSelfCorrection`}>{getLocalizedString("useSelfCorrection")}</InputLabel>
                        <Select
                          disabled={readOnly || formData.questions?.some((question) => question.type === "D")}
                          value={formData.useSelfCorrection ? "Y" : "N"}
                          labelId={`label-useSelfCorrection`}
                          id={`select-useSelfCorrection`}
                          onChange={(e) => setFormValue(e.target.value === "Y", "useSelfCorrection")}
                          margin={"none"}
                        >
                          {
                            getYesNoArray().map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item.key}
                                value={item.key}
                              >
                                {item.value}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                      <Typography className="whiteText" variant="caption">
                        {getLocalizedString("activityUseSelfCorrectionHelperText")}
                      </Typography>
                    </Grid>
                  </>
                }
                {
                  ["P", "E"].includes(formData.type) &&
                  <>
                    <Grid container item alignItems="flex-start" xs={12} sm={6} style={gridStyle}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel id={`label-isInPersonActivity`}>{getLocalizedString("isInPersonActivity")}</InputLabel>
                        <Select
                          disabled={readOnly}
                          value={formData.isInPersonActivity ? "Y" : "N"}
                          labelId={`label-isInPersonActivity`}
                          id={`select-isInPersonActivity`}
                          onChange={(e) => setFormValue(e.target.value === "Y", "isInPersonActivity")}
                          margin={"none"}
                        >
                          {
                            getYesNoArray().map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item.key}
                                value={item.key}
                              >
                                {item.value}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                      <TextField
                        disabled={readOnly}
                        required
                        fullWidth
                        margin={"none"}
                        label={getLocalizedString("attempts")}
                        InputProps={{ inputComponent: CustomNumberFormat }}
                        value={formData.attempts}
                        onChange={(e) => setFormValue(e.target.value, "attempts")}
                        inputProps={{ min: 0, max: 999, maxLength: 3, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 999 }}
                      />
                    </Grid>
                    {
                      formData.isInPersonActivity && formData.attempts > 0 &&
                      <>
                        {
                          Array.from({ length: formData.attempts }, (_, i) => i + 1).map((_, attemptIndex) => {
                            return (
                              <Card key={attemptIndex} style={{ width: "100%", padding: 16, marginBottom: 10 }}>
                                <Grid container item alignItems="center" xs={12} style={gridStyle}>
                                  <Typography className="whiteText" style={{ marginRight: 10, fontWeight: 500 }}>
                                    {`${getLocalizedString("attempt")}: ${attemptIndex + 1}`}
                                  </Typography>
                                </Grid>
                                <Grid container item alignItems="flex-start" xs={12}>
                                  <Grid container item alignItems="center" xs={6} sm={2} style={gridStyle}>
                                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                      <DatePicker
                                        disabled={readOnly}
                                        fullWidth
                                        label={getLocalizedString("dateFrom")}
                                        clearLabel={getLocalizedString("clear")}
                                        okLabel={getLocalizedString("ok")}
                                        invalidDateMessage=''
                                        format="dd/MM/yyyy"
                                        margin={'none'}
                                        value={formData.attemptsConfig?.[attemptIndex]?.date || null}
                                        onChange={(e) => setAttemptItem(attemptIndex, { ...(formData.attemptsConfig?.[attemptIndex] || {}), date: e })}
                                      />
                                    </MuiPickersUtilsProvider>
                                  </Grid>
                                  <Grid container item alignItems="center" xs={6} sm={2} style={gridStyle}>
                                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                      <TimePicker
                                        disabled={readOnly}
                                        fullWidth
                                        ampm={false}
                                        label={getLocalizedString("time")}
                                        value={formData.attemptsConfig?.[attemptIndex]?.time || null}
                                        onChange={(e) => setAttemptItem(attemptIndex, { ...(formData.attemptsConfig?.[attemptIndex] || {}), time: e })}
                                        cancelLabel={getLocalizedString("cancel")}
                                        invalidDateMessage=""
                                      />
                                    </MuiPickersUtilsProvider>
                                  </Grid>
                                  <Grid container item alignItems="center" xs={12} sm={8} style={gridStyle}>
                                    <TextField
                                      disabled={readOnly}
                                      fullWidth
                                      margin={"none"}
                                      label={getLocalizedString("location")}
                                      value={formData.attemptsConfig?.[attemptIndex]?.location || ""}
                                      onChange={(e) => setAttemptItem(attemptIndex, { ...(formData.attemptsConfig?.[attemptIndex] || {}), location: e.target.value.substring(0, 254) })}
                                      helperText={getLocalizedString("enterLinkOrAddress")}
                                    />
                                  </Grid>
                                </Grid>
                              </Card>
                            )
                          })
                        }
                      </>
                    }
                  </>
                }
                {
                  ["L", "P", "E", "S"].includes(formData.type) &&
                  <Grid container item alignItems="center" xs={12} style={gridStyle}>
                    <InputLabel shrink required>
                      {getLocalizedString("content")}
                    </InputLabel>
                    <div style={{ width: "100%", marginTop: 5 }}>
                      <TextEditor
                        disabled={readOnly}
                        initialValue={formData.content}
                        placeholder={getLocalizedString("enterContent")}
                        handleChange={(value) => setFormValue(value, "content")}
                        allowImages
                      />
                    </div>
                  </Grid>
                }
                {
                  ["M"].includes(formData.type) &&
                  <>
                    <Grid container item alignItems="center" xs={12} style={gridStyle}>
                      <TextField
                        disabled={readOnly}
                        fullWidth
                        margin={"none"}
                        label={getLocalizedString("externalMedia")}
                        value={formData.mediaUrl}
                        onChange={(e) => setFormValue(e.target.value, "mediaUrl")}
                      />
                    </Grid>
                    <Grid container item alignItems="center" xs={12} style={gridStyle}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel id={`label-mediaId`}>{getLocalizedString("selectMediaFromLibrary")}</InputLabel>
                        <Select
                          disabled={readOnly}
                          value={formData.mediaId}
                          labelId={`label-mediaId`}
                          id={`select-mediaId`}
                          onChange={(e) => setFormValue(e.target.value, "mediaId")}
                          margin={"none"}
                        >
                          {
                            media.map((item) => (
                              <MenuItem
                                className={"whiteText"}
                                key={item.key}
                                value={item.key}
                              >
                                {item.value}
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid container item alignItems="center" xs={12} style={gridStyle}>
                      <FileInput
                        disabled={readOnly}
                        label={getLocalizedString("uploadFile")}
                        acceptedFormats={[".mp4", ".webm", ".mov", ".mkv", ".mp3", ".wav"]}
                        handleChange={(files) => setFormValue(files, "mediaFile")}
                        preSubmitCallback={handleClearInvalidFiles}
                        id={"mediaFile"}
                      />
                      <FileList
                        disabled={readOnly}
                        files={formData.mediaFile}
                        filesWithError={invalidFiles}
                        handleClick={() => setFormValue(null, "mediaFile")}
                      />
                    </Grid>
                  </>
                }
              </>
            }
            {
              ["S"].includes(formData.type) &&
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <Grid container item alignItems="center" xs={12}>
                  <Typography className="whiteText" style={{ paddingBottom: 10 }}>
                    {getLocalizedString("sessions")}
                  </Typography>
                </Grid>
                {
                  formData.sessions.map((session, sessionIndex) => {
                    return (
                      <Card key={sessionIndex} style={{ width: "100%", padding: 16, marginBottom: 10 }}>
                        <Grid container item alignItems="center" xs={12} style={gridStyle}>
                          <Typography className="whiteText" style={{ marginRight: 10, fontWeight: 500 }}>
                            {`${getLocalizedString("session")}: ${sessionIndex + 1}`}
                          </Typography>
                        </Grid>
                        <Grid container item alignItems="center" xs={12}>
                          <Grid container item alignItems="flex-start" xs={readOnly ? 12 : 11}>
                            <Grid container item alignItems="center" xs={6} sm={4} style={gridStyle}>
                              <FormControl style={{ flex: 1, marginRight: 10 }}>
                                <InputLabel required id={`label-type-${sessionIndex}`}>{getLocalizedString("sessionType")}</InputLabel>
                                <Select
                                  disabled={readOnly}
                                  value={session.type || ""}
                                  labelId={`label-type-${sessionIndex}`}
                                  id={`select-type-${sessionIndex}`}
                                  onChange={(e) => setSessionItem(sessionIndex, { ...session, type: e.target.value })}
                                  margin={"none"}
                                >
                                  {
                                    sessionTypes.map((item) => (
                                      <MenuItem
                                        className={"whiteText"}
                                        key={item.key}
                                        value={item.key}
                                      >
                                        {item.value}
                                      </MenuItem>
                                    ))
                                  }
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid container item alignItems="center" xs={6} sm={4} style={gridStyle}>
                              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                <DatePicker
                                  disabled={readOnly}
                                  fullWidth
                                  label={getLocalizedString("dateFrom")}
                                  clearLabel={getLocalizedString("clear")}
                                  okLabel={getLocalizedString("ok")}
                                  invalidDateMessage=''
                                  format="dd/MM/yyyy"
                                  margin={'none'}
                                  value={session.date || null}
                                  onChange={(e) => setSessionItem(sessionIndex, { ...session, date: e })}
                                />
                              </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid container item alignItems="center" xs={6} sm={4} style={gridStyle}>
                              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                <TimePicker
                                  disabled={readOnly}
                                  fullWidth
                                  ampm={false}
                                  label={getLocalizedString("time")}
                                  value={session.time || null}
                                  onChange={(e) => setSessionItem(sessionIndex, { ...session, time: e })}
                                  cancelLabel={getLocalizedString("cancel")}
                                  invalidDateMessage=""
                                />
                              </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                              <TextField
                                disabled={readOnly}
                                fullWidth
                                margin={"none"}
                                label={getLocalizedString("location")}
                                value={session.location || ""}
                                onChange={(e) => setSessionItem(sessionIndex, { ...session, location: e.target.value.substring(0, 254) })}
                                helperText={getLocalizedString("enterLinkOrAddress")}
                              />
                            </Grid>
                            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                              <TextField
                                disabled={readOnly}
                                fullWidth
                                margin={"none"}
                                label={getLocalizedString("additionalInfo")}
                                value={session.additionalInfo || ""}
                                onChange={(e) => setSessionItem(sessionIndex, { ...session, additionalInfo: e.target.value.substring(0, 254) })}
                              />
                            </Grid>
                          </Grid>
                          {
                            !readOnly &&
                            <Grid container item alignItems="center" justify="center" xs={1}>
                              <IconButton size="small" onClick={() => deleteSessionItem(sessionIndex)}>
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          }
                        </Grid>
                      </Card>
                    )
                  })
                }
                {
                  !readOnly &&
                  <Button
                    style={{ margin: "0 auto" }}
                    className="whiteText"
                    variant="contained"
                    onClick={() => setSessionItem(formData.sessions.length, {})}
                  >
                    {getLocalizedString("addSession")}
                  </Button>
                }
              </Grid>
            }
          </Grid>
        </DialogContent>
        <DialogActions>
          {
            !readOnly &&
            <>
              <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
              <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
            </>
          }
          {readOnly && <ButtonDialogAction onClick={handleClose} isAccept text={getLocalizedString("close")} />}
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default ActivityFormDialog;